import React, { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import { Dropdown } from '@yme/atoms/dropdown';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { SELECT_CATEGORY_DROPDOWN_ID, SELECT_MODEL_YEAR_DROPDOWN_ID, SELECT_MODEL_DROPDOWN_ID, DROPDOWN_ACTIVE_PADDING_VALUE, } from './constants';
import { checkAllCategoriesYearFilterDisabled, findProductByKey, getIsEndOfScroll, prepareDropdownProductData, sortingByCategory, sortingByProductIndex, } from './helpers';
import { getProductByType } from './services';
export function SearchByType(props) {
    const { isComparisonModal, productTypeId, productsByQueryEnd, comparingProductsByQuery, categories, categoriesValues, setSelectedProduct, isSegmentsExist, defaultStartYear, labels, setIsLoading, isRightToLeft, } = props;
    const [selectedCategoryValue, setSelectedCategoryValue] = useState(isComparisonModal && !isSegmentsExist ? head(categories)?.inRiverId || '' : '');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedDateValue, setSelectedDateValue] = useState('');
    const [dateDropdownItems, setDateDropdownItems] = useState([]);
    const [productResult, setProductResult] = useState([]);
    const [currentProductResult, setCurrentProductResult] = useState(null);
    const [isSelectModelDisabled, setIsSelectModelDisabled] = useState(true);
    const [selectModelScrollHeight, setSelectModelScrollHeight] = useState(0);
    const [productOffsetParam, setProductOffsetParam] = useState(0);
    const { isInfiniteDesktopView } = useWindowSize();
    const currentCategory = categories.find((category) => category.inRiverId === selectedCategoryValue);
    const startFromYear = currentCategory?.startFromYear;
    const yearRangeIfYearFilterDisabled = useMemo(() => {
        const nextYear = String(new Date().getFullYear() + 1);
        const previousYear = String(new Date().getFullYear() - 1);
        const hasStartFromYear = startFromYear !== -1;
        return `range ("${hasStartFromYear ? startFromYear : previousYear}" to "${nextYear}")`;
    }, [startFromYear]);
    const isYearFilterSelectModelDisabled = !isComparisonModal && currentCategory?.yearFilterSelectModelDisabled;
    const isComparisonYearFilterDisabled = useMemo(() => {
        if (isComparisonModal) {
            if (currentCategory?.yearFilterDisabled ||
                (isSegmentsExist && checkAllCategoriesYearFilterDisabled(categories))) {
                return true;
            }
        }
        return false;
    }, [categories, currentCategory?.yearFilterDisabled, isComparisonModal, isSegmentsExist]);
    const hasRequestProductLimit = isComparisonModal
        ? !isComparisonYearFilterDisabled
        : !isYearFilterSelectModelDisabled;
    const isYearFilterShowed = useMemo(() => {
        if (isComparisonModal) {
            return !isComparisonYearFilterDisabled;
        }
        return !isYearFilterSelectModelDisabled;
    }, [isComparisonModal, isComparisonYearFilterDisabled, isYearFilterSelectModelDisabled]);
    const productsByQueryEndWithParams = useMemo(() => `${!isYearFilterShowed && comparingProductsByQuery
        ? comparingProductsByQuery
        : productsByQueryEnd}${encodeURIComponent(`categories.id:subtree("${selectedCategoryId}")|productType.id:"${productTypeId}"|variants.attributes.productYear:${!isYearFilterShowed ? selectedDateValue : `"${selectedDateValue}"`}`)}`, [
        comparingProductsByQuery,
        isYearFilterShowed,
        productTypeId,
        productsByQueryEnd,
        selectedCategoryId,
        selectedDateValue,
    ]);
    const categoriesDropdownItems = useMemo(() => {
        const preparedCategories = categories?.map((category) => ({
            value: category.inRiverId,
            label: category.title,
        }));
        if (isComparisonModal) {
            if (isSegmentsExist) {
                return preparedCategories;
            }
            else {
                return null;
            }
        }
        return preparedCategories;
    }, [categories, isComparisonModal, isSegmentsExist]);
    const getDateDropdownItems = useCallback(() => {
        const startYear = startFromYear && startFromYear !== -1 ? startFromYear : defaultStartYear;
        const dateArrayRangeLength = new Date().getFullYear() - startYear + 2;
        const dateRange = [...Array(dateArrayRangeLength)].map((date, index) => startYear + index);
        return dateRange.map((date) => ({
            label: String(date),
            value: String(date),
        }));
    }, [defaultStartYear, startFromYear]);
    const getProductData = useCallback((endpoint, offset = 0, subCategoryIds = [], isSavePreviousState = false) => {
        setIsLoading(true);
        getProductByType(endpoint, offset, hasRequestProductLimit)
            .then((data) => {
            setProductResult((prevState) => {
                if (hasRequestProductLimit) {
                    return isSavePreviousState
                        ? sortingByProductIndex([...prevState, ...data])
                        : sortingByProductIndex(data);
                }
                return isSegmentsExist
                    ? sortingByProductIndex(data)
                    : sortingByCategory({
                        products: data,
                        externalIds: subCategoryIds,
                    });
            });
        })
            .finally(() => {
            setIsLoading(false);
        });
    }, [setIsLoading, hasRequestProductLimit, isSegmentsExist]);
    const onChangeModelDropdown = useCallback((value, label = '') => {
        const currentProduct = findProductByKey(productResult, value);
        setCurrentProductResult({
            value,
            label,
        });
        setSelectedProduct(currentProduct);
        if (!isComparisonModal && currentProduct) {
            setDigitalData(DigitalDataMutation.COMPATIBILITY_SELECT_PRODUCT, {
                productId: currentProduct?.slug,
                term: '',
            });
        }
    }, [isComparisonModal, productResult, setSelectedProduct]);
    const resetStateParams = useCallback(() => {
        setSelectedProduct(null);
        setCurrentProductResult(null);
        setProductResult([]);
        setSelectModelScrollHeight(0);
        setProductOffsetParam(0);
    }, [setSelectedProduct]);
    const onChangeCategoryDropdown = useCallback((value) => {
        resetStateParams();
        setSelectedDateValue('');
        setSelectedCategoryValue(value);
    }, [resetStateParams]);
    const onChangeDateDropdown = useCallback((value) => {
        resetStateParams();
        setSelectedDateValue(value);
    }, [resetStateParams]);
    const onScrollSelectModelDropdown = useCallback((scrollRef) => {
        const scrollCurrent = scrollRef.current;
        if (scrollCurrent) {
            const isEndOfScroll = getIsEndOfScroll(scrollCurrent);
            const scrollHeight = scrollCurrent.scrollTop;
            if (isEndOfScroll && scrollHeight > selectModelScrollHeight) {
                setSelectModelScrollHeight(scrollHeight);
                setProductOffsetParam((prev) => prev + 10);
            }
        }
    }, [selectModelScrollHeight]);
    useEffect(() => {
        if (!isYearFilterShowed && selectedCategoryId) {
            setSelectedDateValue(yearRangeIfYearFilterDisabled);
        }
    }, [isYearFilterShowed, selectedCategoryId, yearRangeIfYearFilterDisabled]);
    useEffect(() => {
        if (categoriesValues && selectedCategoryValue) {
            const currentCategoryId = categoriesValues.find((category) => category.externalId === selectedCategoryValue)?.id;
            if (currentCategoryId) {
                setSelectedCategoryId((prev) => (prev === currentCategoryId ? prev : currentCategoryId));
                if (isYearFilterShowed) {
                    setDateDropdownItems(getDateDropdownItems());
                }
            }
        }
    }, [
        categoriesValues,
        selectedCategoryValue,
        getDateDropdownItems,
        isComparisonYearFilterDisabled,
        isYearFilterShowed,
    ]);
    useEffect(() => {
        if (selectedCategoryId && selectedDateValue) {
            const subCategoryIds = currentCategory?.sortingRules;
            getProductData(productsByQueryEndWithParams, productOffsetParam, subCategoryIds, productOffsetParam !== 0);
        }
    }, [
        currentCategory?.sortingRules,
        getProductData,
        productOffsetParam,
        productsByQueryEndWithParams,
        selectedCategoryId,
        selectedDateValue,
    ]);
    useEffect(() => {
        setIsSelectModelDisabled(!productResult.length);
    }, [productResult.length]);
    return (React.createElement(React.Fragment, null,
        categoriesDropdownItems && (React.createElement(Dropdown, { id: SELECT_CATEGORY_DROPDOWN_ID, activeItem: selectedCategoryValue, onChange: onChangeCategoryDropdown, items: categoriesDropdownItems, inputPlaceholder: get(labels, 'selectCategoryPlaceholder'), labelText: get(labels, 'selectCategoryLabel'), isRightToLeft: isRightToLeft, useForMobile: true })),
        isYearFilterShowed && (React.createElement(Dropdown, { id: SELECT_MODEL_YEAR_DROPDOWN_ID, activeItem: selectedDateValue, items: dateDropdownItems, onChange: onChangeDateDropdown, inputPlaceholder: get(labels, 'selectYearPlaceholder'), labelText: get(labels, 'selectYearLabel'), isRightToLeft: isRightToLeft, useForMobile: true })),
        React.createElement(Dropdown, { id: SELECT_MODEL_DROPDOWN_ID, activeItem: currentProductResult?.value || '', items: prepareDropdownProductData(productResult), isFlexible: true, className: "items-with-icons", paddingValue: currentProductResult?.value ? DROPDOWN_ACTIVE_PADDING_VALUE : undefined, isNoResultComponentShowed: true, noResultTitle: get(labels, 'searchInputNoResultsHeader'), noResultDescription: get(labels, 'searchInputNoResultsHeader'), isDisabled: isSelectModelDisabled, hasAutocomplete: isInfiniteDesktopView, onScroll: hasRequestProductLimit ? onScrollSelectModelDropdown : undefined, onChange: onChangeModelDropdown, inputPlaceholder: get(labels, 'selectModelPlaceholder'), labelText: get(labels, 'selectModelLabel'), isRightToLeft: isRightToLeft, useForMobile: true })));
}
